import { useBackoffice } from '../../backoffice/useBackoffice';
import { Button, Flex, Table, Typography, Upload } from 'antd';
import { KycDoc, useKycDocuments } from './useKycDocuments';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDownloadFile } from '../../libs/file/useDownloadFile';
import { css } from '@emotion/css';
import { PencilIcon } from '@heroicons/react/24/outline';
import { EditDocumentModal } from './EditDocumentModal';

const { Column } = Table;

export const DocumentsSection: FC<{ customerId: string }> = ({ customerId }) => {
  const { isBackofficeOn } = useBackoffice();
  const query = useKycDocuments(customerId);
  const [docToEdit, setDocToEdit] = useState<KycDoc | null>(null);

  if (!isBackofficeOn) {
    return null;
  }

  return (
    <>
      <Table
        loading={query.isLoading}
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Documents
              </Typography.Title>
            </Flex>
          );
        }}
        pagination={false}
        dataSource={query.data?.items || []}
      >
        <Column
          title="Type"
          width={260}
          key="type"
          render={(v: KycDoc) => {
            return v.doc_type;
          }}
        />

        <Column
          title="Sub Type"
          width={260}
          key="doc_subtype"
          render={(v: KycDoc) => {
            return v.doc_subtype;
          }}
        />

        <Column
          title="Side"
          width={260}
          key="doc_side"
          render={(v: KycDoc) => {
            return v.doc_side || '-';
          }}
        />

        <Column
          title="File"
          key="document"
          render={(v: KycDoc) => {
            return <FilePreview customerId={customerId} fileKey={v.bucket_file_key} />;
          }}
        />

        <Column
          title="Actions"
          key="actions"
          className={css`
            text-align: right;
          `}
          render={(v: KycDoc) => {
            return (
              <Button
                type="text"
                icon={<PencilIcon width={16} />}
                onClick={() => {
                  setDocToEdit(v);
                }}
              ></Button>
            );
          }}
        />
      </Table>

      {docToEdit && (
        <EditDocumentModal
          doc={docToEdit}
          customerId={customerId}
          open
          onClose={() => {
            setDocToEdit(null);
          }}
        />
      )}
    </>
  );
};

const FilePreview: FC<{ customerId: string; fileKey: string }> = ({ customerId, fileKey }) => {
  const downloadFileQuery = useDownloadFile(customerId, fileKey);
  const previewUrl = useMemo(() => {
    if (!downloadFileQuery.data) return undefined;

    try {
      const base64Data = downloadFileQuery.data.base64FileData;
      const contentType = downloadFileQuery.data.contentType;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });

      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error converting base64 to blob URL:', error);
      return undefined;
    }
  }, [downloadFileQuery.data]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (downloadFileQuery.isLoading) {
    return <>loading...</>;
  }

  return (
    <Upload
      listType="text"
      fileList={[
        {
          uid: fileKey,
          name: `Document`,
          status: 'done',
          url: previewUrl,
        },
      ]}
      action={`${process.env.REACT_APP_SERVER_URL}/api/upload/image`}
    />
  );
};
